import type { Contractor } from '@/services/contractorService'
import type { UpdateContractorPayload } from '@/services/contractorService'

/** Default CNAME target shown in DNS setup instructions. */
export function getPortalCnameTarget(): string {
  const raw = import.meta.env.VITE_PORTAL_CNAME_TARGET
  if (typeof raw === 'string' && raw.trim()) return raw.trim().toLowerCase()
  return 'portal.valt.africa'
}

export interface ClientPortalDnsSettings {
  portal_host: string
  /** Short slug for {slug}.valt.africa when not using a fully custom domain. */
  valt_subdomain: string
  custom_domains: string[]
  primary_color: string
  secondary_color: string
  accent_color: string
  login_hint: string
  admin_notes: string
}

export interface DnsRecordRow {
  type: 'CNAME' | 'TXT'
  host: string
  value: string
  notes?: string
}

function metadataRecord(c: Contractor | null | undefined): Record<string, unknown> | null {
  const raw = c?.metadata as unknown
  if (raw == null) return null
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw)
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
        ? (parsed as Record<string, unknown>)
        : null
    } catch {
      return null
    }
  }
  if (typeof raw === 'object' && !Array.isArray(raw)) return raw as Record<string, unknown>
  return null
}

function pickString(...values: unknown[]): string {
  for (const v of values) {
    if (typeof v === 'string' && v.trim()) return v.trim()
  }
  return ''
}

/** Normalize hostname: lowercase, strip protocol/path, trim trailing dot. */
export function normalizePortalHost(input: string): string {
  let host = input.trim().toLowerCase()
  host = host.replace(/^https?:\/\//, '')
  host = host.split('/')[0] ?? host
  host = host.split(':')[0] ?? host
  return host.replace(/\.$/, '')
}

export function parseDomainsList(text: string): string[] {
  return text
    .split(/[\n,;]+/)
    .map(normalizePortalHost)
    .filter(Boolean)
}

export function formatDomainsList(domains: string[]): string {
  return domains.filter(Boolean).join('\n')
}

function parseCustomDomains(raw: unknown): string[] {
  if (Array.isArray(raw)) {
    return raw.map((d) => normalizePortalHost(String(d))).filter(Boolean)
  }
  if (typeof raw === 'string' && raw.trim()) {
    return parseDomainsList(raw)
  }
  return []
}

function deriveValtSubdomain(portalHost: string): string {
  const host = normalizePortalHost(portalHost)
  if (!host.endsWith('.valt.africa')) return ''
  const slug = host.replace(/\.valt\.africa$/, '')
  return slug.includes('.') ? '' : slug
}

/** Read portal DNS / white-label settings from a client record. */
export function readPortalDnsSettings(c: Contractor | null | undefined): ClientPortalDnsSettings {
  const md = metadataRecord(c)
  const record = c as Record<string, unknown> | null | undefined

  const portal_host = pickString(
    record?.portal_host,
    record?.portalHost,
    record?.portal_domain,
    record?.portalDomain,
    md?.portal_host,
    md?.portalHost,
    md?.portal_domain,
  )
  const valt_subdomain =
    pickString(record?.valt_subdomain, record?.valtSubdomain, md?.valt_subdomain, md?.valtSubdomain) ||
    deriveValtSubdomain(portal_host)

  const customFromTop = parseCustomDomains(record?.custom_domains ?? record?.customDomains)
  const customFromMeta = parseCustomDomains(md?.custom_domains ?? md?.customDomains)
  const custom_domains = [...new Set([...customFromTop, ...customFromMeta])].filter((d) => d !== portal_host)

  return {
    portal_host,
    valt_subdomain,
    custom_domains,
    primary_color: pickString(record?.primary_color, record?.primaryColor, md?.primary_color, md?.primaryColor),
    secondary_color: pickString(
      record?.secondary_color,
      record?.secondaryColor,
      md?.secondary_color,
      md?.secondaryColor,
    ),
    accent_color: pickString(record?.accent_color, record?.accentColor, md?.accent_color, md?.accentColor),
    login_hint: pickString(record?.login_hint, record?.loginHint, md?.login_hint, md?.loginHint),
    admin_notes: pickString(
      record?.portal_dns_notes,
      md?.portal_dns_notes,
      md?.dns_notes,
      md?.admin_notes,
    ),
  }
}

/** All hostnames that should resolve to this client's portal. */
export function getClientPortalHosts(settings: ClientPortalDnsSettings): string[] {
  const hosts = new Set<string>()
  if (settings.portal_host) hosts.add(normalizePortalHost(settings.portal_host))
  if (settings.valt_subdomain) {
    hosts.add(normalizePortalHost(`${settings.valt_subdomain}.valt.africa`))
  }
  for (const d of settings.custom_domains) {
    hosts.add(normalizePortalHost(d))
  }
  return [...hosts]
}

export function getPortalLoginUrl(host: string, https = true): string {
  const normalized = normalizePortalHost(host)
  if (!normalized) return ''
  return `${https ? 'https' : 'http'}://${normalized}/clients/login`
}

/** Build DNS rows for client IT teams. */
export function buildDnsRecordRows(settings: ClientPortalDnsSettings): DnsRecordRow[] {
  const cnameTarget = getPortalCnameTarget()
  const rows: DnsRecordRow[] = []

  const customHosts = settings.custom_domains.filter(Boolean)
  if (settings.portal_host && !settings.portal_host.endsWith('.valt.africa')) {
    customHosts.unshift(settings.portal_host)
  }

  for (const host of [...new Set(customHosts)]) {
    const parts = host.split('.')
    const isApex = parts.length === 2
    rows.push({
      type: 'CNAME',
      host: isApex ? '@' : parts[0] ?? host,
      value: cnameTarget,
      notes: isApex
        ? 'Root domains often require ALIAS/ANAME at your DNS provider instead of CNAME.'
        : `Full hostname: ${host}`,
    })
  }

  if (settings.valt_subdomain && !settings.portal_host) {
    rows.push({
      type: 'CNAME',
      host: settings.valt_subdomain,
      value: cnameTarget,
      notes: `Valt subdomain: ${settings.valt_subdomain}.valt.africa (managed on Valt DNS)`,
    })
  }

  if (!rows.length && settings.portal_host) {
    rows.push({
      type: 'CNAME',
      host: settings.portal_host.split('.')[0] ?? settings.portal_host,
      value: cnameTarget,
      notes: settings.portal_host,
    })
  }

  return rows
}

/** PATCH payload for admin client update — merges metadata for backends without dedicated columns. */
export function buildPortalDnsUpdatePayload(
  settings: ClientPortalDnsSettings,
  existing: Contractor | null | undefined,
): UpdateContractorPayload {
  const md = metadataRecord(existing) ?? {}
  const portal_host =
    normalizePortalHost(settings.portal_host) ||
    (settings.valt_subdomain ? normalizePortalHost(`${settings.valt_subdomain}.valt.africa`) : '')

  const custom_domains = settings.custom_domains
    .map(normalizePortalHost)
    .filter((d) => d && d !== portal_host)

  const metadata = {
    ...md,
    portal_host: portal_host || null,
    valt_subdomain: settings.valt_subdomain.trim() || null,
    custom_domains,
    primary_color: settings.primary_color.trim() || null,
    secondary_color: settings.secondary_color.trim() || null,
    accent_color: settings.accent_color.trim() || null,
    login_hint: settings.login_hint.trim() || null,
    portal_dns_notes: settings.admin_notes.trim() || null,
  }

  return {
    portal_host: portal_host || undefined,
    valt_subdomain: settings.valt_subdomain.trim() || undefined,
    custom_domains: custom_domains.length ? custom_domains : undefined,
    primary_color: settings.primary_color.trim() || undefined,
    secondary_color: settings.secondary_color.trim() || undefined,
    accent_color: settings.accent_color.trim() || undefined,
    login_hint: settings.login_hint.trim() || undefined,
    portal_dns_notes: settings.admin_notes.trim() || undefined,
    metadata,
  }
}

/** Short label for list views. */
export function getPortalHostLabel(c: Contractor | null | undefined): string {
  const s = readPortalDnsSettings(c)
  if (s.portal_host) return s.portal_host
  if (s.valt_subdomain) return `${s.valt_subdomain}.valt.africa`
  if (s.custom_domains[0]) return s.custom_domains[0]
  return '—'
}
